import React, { FC, createElement } from 'react';
import { Link } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Action as ActionType } from 'api';
import { generateLink } from 'Links';

const ACTOR_TAG = '{actor}';
const ADJECTIVE_TAG = '{adjective}';

interface IProps {
  action: ActionType;
}

const useStyles = makeStyles(theme => ({
  breakWord: theme.mixins.breakWord,
}));

const Action: FC<IProps> = ({ action }) => {
  const classes = useStyles();
  const { message, substitutions, urls } = action.display;

  const ActorLink = () => (
    <Link
      className={classes.breakWord}
      title={substitutions.actor}
      component={generateLink(urls.actor)}
    >
      {substitutions.actor}
    </Link>
  );

  const AdjectiveLink = () => (
    <Link
      className={classes.breakWord}
      title={substitutions.adjective}
      component={generateLink(urls.adjective)}
    >
      {substitutions.adjective}
    </Link>
  );

  // Convert to react style template tags: {%=actor%} to {actor}
  const reactTemplate = message.replace(/%=|%/g, '');

  // Get replacement indexes
  const actorIndex = reactTemplate.indexOf(ACTOR_TAG);
  const adjectiveIndex = reactTemplate.indexOf(ADJECTIVE_TAG);

  // Determine which replacement comes first
  const firstSubIndex = Math.min(actorIndex, adjectiveIndex);
  const secondSubIndex = Math.max(actorIndex, adjectiveIndex);

  // Replacement offsets and components
  const isActorFirst = firstSubIndex === actorIndex;

  const middleOffset = isActorFirst ? ACTOR_TAG.length : ADJECTIVE_TAG.length;
  const trailingOffset = !isActorFirst ? ACTOR_TAG.length : ADJECTIVE_TAG.length;

  const FirstReplacement = isActorFirst ? ActorLink : AdjectiveLink;
  const SecondReplacement = !isActorFirst ? ActorLink : AdjectiveLink;

  // Include text before and after the first and last replacements
  const leadingPart = reactTemplate.slice(0, firstSubIndex);
  const middlePart = reactTemplate.slice(firstSubIndex + middleOffset, secondSubIndex);
  const trailingPart = reactTemplate.slice(secondSubIndex + trailingOffset, reactTemplate.length);

  const Compile: FC = () => {
    return createElement(
      'span',
      null,
      leadingPart,
      <FirstReplacement />,
      middlePart,
      <SecondReplacement />,
      trailingPart
    );
  };

  return <Compile />;
};

export default Action;
